import { APP_LANGUAGES, getLanguageOptionLabel } from "../../utils/languages";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import toast from "react-hot-toast";

export const REGION_OPTIONS = [
  { value: "healjai", label: "Thailand" },
  { value: "indonesia", label: "Indonesia" },
  { value: "canada", label: "Canada" },
  { value: "uk", label: "UK" },
  { value: "gcc", label: "GCC" },
  { value: "malaysia", label: "Malaysia" },
  { value: "singapore", label: "Singapore" },
  { value: "philippines", label: "Philippines" },
  { value: "vietnam", label: "Vietnam" },
  { value: "mexico", label: "Mexico" },
  { value: "brazil", label: "Brazil" },
  { value: "korea", label: "Korea" },
  { value: "india", label: "India" },
  { value: "japan", label: "Japan" },
  { value: "spanish", label: "Spanish" },
  { value: "us", label: "US" },
];

const Register: React.FC = () => {
  // The landing page profile form can hand over the visitor's first name.
  const location = useLocation();
  const prefillUsername = (location.state as { prefillUsername?: string } | null)
    ?.prefillUsername;
  const [username, setUsername] = useState(prefillUsername ?? "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [roleId, setRoleId] = useState("2");
  const [preferredLanguage, setPreferredLanguage] = useState("th");
  const [region, setRegion] = useState("healjai");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { register } = useAuth();

  // Auto-detect region from IP on mount
  useEffect(() => {
    api
      .get("/detect-region")
      .then((res) => {
        if (res.data?.region) {
          setRegion(res.data.region);
        }
      })
      .catch(() => {
        // silently fall back to healjai default
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) {
      toast.error("Username is required");
      return;
    }

    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }

    if (!password.trim()) {
      toast.error("Password is required");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);

    try {
      await register(
        username,
        email,
        password,
        mobileNo || undefined,
        preferredLanguage,
        roleId,
        region,
      );
      toast.success("Registration successful!");
      navigate("/");
    } catch (error: any) {
      console.error("Registration error in component:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, text: "", color: "" };
    if (password.length < 6)
      return { strength: 25, text: "Weak", color: "bg-red-500" };
    if (password.length < 8)
      return { strength: 50, text: "Fair", color: "bg-yellow-500" };
    if (password.length < 10)
      return { strength: 75, text: "Good", color: "bg-blue-500" };
    return { strength: 100, text: "Strong", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(password);

  const dropdownStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
    backgroundPosition: "right 0.75rem center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "1.5em 1.5em",
    paddingRight: "2.5rem",
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] px-4">
      <div className="w-full max-w-md bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-4xl mb-3">🚀</div>
          <h2 className="text-3xl font-extrabold text-white">Create Account</h2>
          <p className="text-sm text-gray-300 mt-2">
            Already have an account?{" "}
            <Link to="/login" className="text-purple-400 font-medium underline">
              Sign in
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* Username */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              Username *
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="Enter username"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="you@example.com"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              Mobile (Optional)
            </label>
            <input
              type="tel"
              value={mobileNo}
              onChange={(e) => setMobileNo(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="+1 234 567 8900"
            />
          </div>

          {/* Region Dropdown */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">Region *</label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-70"
              style={dropdownStyle}
            >
              {REGION_OPTIONS.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  className="bg-white text-black"
                >
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Auto-detected from your location. You can change this later in your profile.
            </p>
          </div>

          {/* Preferred Language Dropdown */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              Preferred Language *
            </label>
            <select
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none appearance-none cursor-pointer"
              style={dropdownStyle}
            >
              {APP_LANGUAGES.map((language) => (
                <option
                  key={language.code}
                  value={language.code}
                  className="bg-white text-black"
                >
                  {getLanguageOptionLabel(language)}
                </option>
              ))}
            </select>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>

            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex justify-between text-xs text-gray-300 mb-1">
                  <span>Password strength</span>
                  <span>{passwordStrength.text}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded-full">
                  <div
                    className={`${passwordStrength.color} h-1.5 rounded-full transition-all`}
                    style={{ width: `${passwordStrength.strength}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              Confirm Password *
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="••••••••"
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-red-400 mt-1">
                Passwords do not match
              </p>
            )}
          </div>

          <input type="hidden" value={roleId} />

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold shadow-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Creating account..." : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;
